import React, { Suspense } from 'react';
import UnknownFlag from 'assets/countryFlags/unknown.svg?react';
import { Tooltip } from 'components/Tooltip';
import { cn } from 'utils/classNamesTailwind';
import { FLAGS, type FlagCode } from './flags.registry';

const LazyFlags = Object.fromEntries(Object.entries(FLAGS).map(([code, fn]) => [code, React.lazy(fn)])) as Record<
  string,
  React.LazyExoticComponent<React.ComponentType<React.SVGProps<SVGSVGElement>>>
>;

const toHeight = (w: number) => Math.round(w * 0.75);

const DEFAULT_WIDTH = 24;
const DEFAULT_STYLE: React.CSSProperties = {
  width: DEFAULT_WIDTH,
  height: toHeight(DEFAULT_WIDTH),
  overflow: 'hidden',
  display: 'inline-block',
  flexShrink: 0,
};

export interface FlagProps {
  countryCode?: FlagCode | (string & {}) | null;
  tooltip?: string;
  className?: string;
  width?: number;
  title?: string;
}

export function Flag({
  countryCode = undefined,
  tooltip = undefined,
  className = '',
  width = DEFAULT_WIDTH,
  title = undefined,
}: FlagProps) {
  const code = countryCode?.toLowerCase();
  const LazyFlag = code ? LazyFlags[code] : undefined;

  const style = width === DEFAULT_WIDTH ? DEFAULT_STYLE : { ...DEFAULT_STYLE, width, height: toHeight(width) };

  const flag = (
    <span className={cn(tooltip && 'border border-gray-200 rounded-sm', className)} style={style}>
      {LazyFlag ? (
        <Suspense fallback={null}>
          <LazyFlag className="w-full" aria-hidden="true" />
        </Suspense>
      ) : (
        <UnknownFlag className="w-full" aria-label={title ?? tooltip ?? 'Unknown flag'} />
      )}
    </span>
  );

  return tooltip ? (
    <Tooltip tooltipContent={tooltip} placement="auto" className="inline-flex">
      {flag}
    </Tooltip>
  ) : (
    flag
  );
}
